import React from "react";
import {
  Mic,
  Brain,
  ArrowRight,
  Sparkles,
  BookOpen,
  Network,
  Lock,
} from "lucide-react";
import logoIcon from "./assets/logoIcon.png";
import textLogo from "./assets/Text-Logo.png";
import MuseIconWeb from "./assets/MuseIconWeb.png";

const MuseLanding = () => {
  return (
    <div className="min-h-screen relative overflow-hidden bg-[#4473b9]">
      {/* Background gradient overlay */}
      <div className="absolute inset-0 bg-gradient-to-b from-[#282F4B] via-[#4473b9] to-[#4473b9] opacity-90" />

      <div className="relative flex flex-col items-center justify-center px-3 sm:px-4 min-h-screen py-12 sm:py-0">
        <div className="text-center w-full max-w-3xl mx-auto backdrop-blur-sm rounded-xl p-3 sm:p-8 bg-[#282F4B]/10">
          {/* Logo Section - reduced bottom margin */}
          <a href="https://tinycloud.xyz" target="_blank" rel="noopener noreferrer">
            <div className="flex justify-center items-center gap-2 mb-4 sm:mb-6 group">
              <div className="relative transform hover:scale-110 transition-all duration-300">
                <img
                  src={logoIcon}
                  alt="TinyCloud Icon"
                  className="h-8 w-8 sm:h-10 sm:w-10 object-contain"
                />
              </div>
              <img
                src={textLogo}
                alt="TinyCloud"
                className="h-5 sm:h-6 object-contain"
              />
            </div>
          </a>

          {/* App Icon - increased size and margins */}
          <div className="flex justify-center mb-8 sm:mb-10">
            <img
              src={MuseIconWeb}
              alt="Muse AI"
              className="h-24 w-24 sm:h-32 sm:w-32 md:h-40 md:w-40 object-contain rounded-3xl"
            />
          </div>

          {/* Tagline */}
          <p className="font-space-grotesk text-xl sm:text-2xl md:text-3xl text-[#FDF9D2] mb-4 sm:mb-5 max-w-2xl mx-auto leading-snug sm:leading-relaxed px-2 sm:px-4 font-medium">
            Your sovereign context platform. Capture everything, own it forever.
          </p>

          {/* Main Value Proposition */}
          <p className="font-space-grotesk text-base sm:text-lg md:text-xl text-[#FDF9D2]/80 mb-12 sm:mb-14 max-w-2xl mx-auto leading-relaxed px-4 sm:px-6">
            Muse is your sovereign context platform that goes beyond voice notes. Capture in-person meetings, voice memos, and conversations—then organize them into collaborative folders with your team. Add markdown documents, sync with Notion, and build a comprehensive knowledge base that you own completely. Your AI companion understands your full context across all conversations and documents.
          </p>

          {/* Download Buttons */}
          <div className="w-full max-w-xl mx-auto mb-8 sm:mb-10 px-2">
            <div className="flex flex-col sm:flex-row gap-5 justify-center">
              <a
                href="/ios"
                target="_blank"
                rel="noopener noreferrer"
                className="w-full sm:w-auto px-8 py-4 bg-[#4473b9] hover:bg-[#4473b9]/90 text-[#282F4B] font-bold rounded-lg flex items-center justify-center gap-3 transition-all hover:scale-105 text-lg"
              >
                <Sparkles className="h-5 w-5" />
                Download for iOS
                <ArrowRight className="h-5 w-5" />
              </a>
              <a
                href="/world"
                target="_blank"
                rel="noopener noreferrer"
                className="w-full sm:w-auto px-8 py-4 bg-[#4473b9] hover:bg-[#4473b9]/90 text-[#282F4B] font-bold rounded-lg flex items-center justify-center gap-3 transition-all hover:scale-105 text-lg"
              >
                <Sparkles className="h-5 w-5" />
                Open in World App
                <ArrowRight className="h-5 w-5" />
              </a>
            </div>
            <div className="mt-4 flex flex-col sm:flex-row justify-center gap-3">
              <a
                href="/android"
                target="_blank"
                rel="noopener noreferrer"
                className="px-5 py-2 bg-[#4473b9]/80 hover:bg-[#4473b9]/90 text-[#282F4B] font-medium rounded-lg flex items-center justify-center gap-2 transition-all hover:scale-105 text-sm"
              >
                Android Beta Access
                <ArrowRight className="h-4 w-4" />
              </a>
              <a
                href="https://t.me/+pplkv1XbbU01MDVh"
                target="_blank"
                rel="noopener noreferrer"
                className="px-5 py-2 bg-[#4473b9]/80 hover:bg-[#4473b9]/90 text-[#282F4B] font-medium rounded-lg flex items-center justify-center gap-2 transition-all hover:scale-105 text-sm"
              >
                Join Telegram
                <ArrowRight className="h-4 w-4" />
              </a>
            </div>
          </div>

          {/* Features Grid - increased padding and gaps */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-6 sm:gap-10 mb-16 sm:mb-20 px-4 sm:px-6">
            {[
              {
                icon: Mic,
                title: "Capture Everything, Everywhere",
                desc: "Record in-person and online meetings in any language. Live transcription with speaker identification. Organize into shared folders for team collaboration—even access meetings you missed.",
              },
              {
                icon: Network,
                title: "Lifetime Memory",
                desc: "Voice recordings, markdown files, and meeting notes—all in one place. Build your personal knowledge base with complete privacy and encryption. Your data, your control.",
              },
              {
                icon: Brain,
                title: "Third-Party Vision AI",
                desc: "Chat with folders to query across all your conversations. Ask questions mid-meeting and get answers from past discussions—even ones you weren't part of. AI that knows your full context.",
              },
            ].map(({ icon: Icon, title, desc }) => (
              <div
                key={title}
                className="flex flex-col items-center group p-6 sm:p-8 bg-[#282F4B]/10 rounded-lg backdrop-blur-sm border border-[#4473b9]/20 hover:border-[#4473b9]/40 hover:bg-[#282F4B]/20 transition-all duration-300"
              >
                <div className="relative mb-4 sm:mb-5">
                  <Icon className="h-6 w-6 sm:h-8 sm:w-8 text-[#4473b9]" />
                  <div className="absolute inset-0 animate-ping opacity-50 group-hover:opacity-100">
                    <Icon className="h-6 w-6 sm:h-8 sm:w-8 text-[#4473b9]" />
                  </div>
                </div>
                <h3 className="font-medium text-[#FDF9D2] mb-3 text-lg sm:text-xl">
                  {title}
                </h3>
                <p className="text-sm sm:text-base text-[#FDF9D2]/80 text-center leading-relaxed">
                  {desc}
                </p>
              </div>
            ))}
          </div>

          {/* Why Muse vs. Notion Section */}
          <div className="text-center mb-16 sm:mb-20 px-4">
            <h2 className="text-2xl sm:text-3xl font-bold text-[#FDF9D2] mb-10">
              More Than Just Documents
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-6 max-w-3xl mx-auto">
              <div className="p-6 bg-[#282F4B]/20 rounded-lg backdrop-blur-sm border border-[#4473b9]/20 hover:border-[#4473b9]/40 transition-all duration-300">
                <p className="text-[#FDF9D2] text-lg font-medium mb-2">📹 In-Person + Online Meetings</p>
                <p className="text-[#FDF9D2]/70 text-sm">Not just documents, but real conversations</p>
              </div>
              <div className="p-6 bg-[#282F4B]/20 rounded-lg backdrop-blur-sm border border-[#4473b9]/20 hover:border-[#4473b9]/40 transition-all duration-300">
                <p className="text-[#FDF9D2] text-lg font-medium mb-2">🔒 Privacy-First</p>
                <p className="text-[#FDF9D2]/70 text-sm">Encrypted and sovereign. You control your data</p>
              </div>
              <div className="p-6 bg-[#282F4B]/20 rounded-lg backdrop-blur-sm border border-[#4473b9]/20 hover:border-[#4473b9]/40 transition-all duration-300">
                <p className="text-[#FDF9D2] text-lg font-medium mb-2">🤖 AI-Native</p>
                <p className="text-[#FDF9D2]/70 text-sm">Built for context from day one, not bolted on</p>
              </div>
            </div>
          </div>

          {/* Future Vision Section */}
          <div className="text-center mb-16 sm:mb-20 px-4">
            <h2 className="text-2xl sm:text-3xl font-bold text-[#FDF9D2] mb-3">
              Future Vision
            </h2>
            <p className="text-[#FDF9D2]/70 mb-10 text-sm sm:text-base">
              Features launching soon
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 max-w-2xl mx-auto">
              <div className="bg-gradient-to-br from-[#282F4B]/40 to-[#4473b9]/20 backdrop-blur-sm rounded-xl p-6 border border-[#4473b9]/20 hover:border-[#4473b9]/40 transition-all duration-500 group hover:shadow-lg hover:shadow-[#4473b9]/10">
                <h3 className="text-[#FDF9D2] text-lg font-semibold tracking-wide mb-4">
                  Third-Party Vision
                </h3>
                <p className="text-[#FDF9D2]/80 text-sm sm:text-base text-center leading-relaxed">
                  Engage AI during live conversations. Ask questions and get answers pulled from your entire conversation history—combining current and past context in real-time.
                </p>
              </div>

              <div className="bg-gradient-to-br from-[#282F4B]/40 to-[#4473b9]/20 backdrop-blur-sm rounded-xl p-6 border border-[#4473b9]/20 hover:border-[#4473b9]/40 transition-all duration-500 group hover:shadow-lg hover:shadow-[#4473b9]/10">
                <h3 className="text-[#FDF9D2] text-lg font-semibold tracking-wide mb-4">
                  World Builder Integration
                </h3>
                <p className="text-[#FDF9D2]/80 text-sm sm:text-base text-center leading-relaxed">
                  Talk your ideas into existence. Your brainstorming sessions automatically become context for building functional web apps—from conversation to creation.
                </p>
              </div>

              <div className="bg-gradient-to-br from-[#282F4B]/40 to-[#4473b9]/20 backdrop-blur-sm rounded-xl p-6 border border-[#4473b9]/20 hover:border-[#4473b9]/40 transition-all duration-500 group hover:shadow-lg hover:shadow-[#4473b9]/10">
                <h3 className="text-[#FDF9D2] text-lg font-semibold tracking-wide mb-4">
                  Notion Sync
                </h3>
                <p className="text-[#FDF9D2]/80 text-sm sm:text-base text-center leading-relaxed">
                  Bidirectional sync with Notion. Use Muse as your meeting layer while maintaining your existing workflows and documentation systems.
                </p>
              </div>

              <div className="bg-gradient-to-br from-[#282F4B]/40 to-[#4473b9]/20 backdrop-blur-sm rounded-xl p-6 border border-[#4473b9]/20 hover:border-[#4473b9]/40 transition-all duration-500 group hover:shadow-lg hover:shadow-[#4473b9]/10">
                <h3 className="text-[#FDF9D2] text-lg font-semibold tracking-wide mb-4">
                  Team RAG Folders
                </h3>
                <p className="text-[#FDF9D2]/80 text-sm sm:text-base text-center leading-relaxed">
                  Collaborative AI that learns from your team's conversations. Query across multiple meetings and get insights from discussions you weren't part of.
                </p>
              </div>
            </div>
          </div>

          {/* Bottom Links */}
          <div className="flex justify-center gap-4 text-sm">
            <a
              href="/"
              target="_blank"
              rel="noopener noreferrer"
              className="text-[#FDF9D2]/60 hover:text-[#FDF9D2]/80 underline"
            >
              Back to TinyCloud
            </a>
            <a
              href="/privacy"
              target="_blank"
              rel="noopener noreferrer"
              className="text-[#FDF9D2]/60 hover:text-[#FDF9D2]/80 underline"
            >
              Privacy Policy
            </a>
            <a
              href="https://t.me/+pplkv1XbbU01MDVh"
              target="_blank"
              rel="noopener noreferrer"
              className="text-[#FDF9D2]/60 hover:text-[#FDF9D2]/80 underline"
            >
              Telegram
            </a>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MuseLanding;
